import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { takeUntil } from 'rxjs/operators';
import { ClientLockService } from 'src/app/service/client-lock-service/client-lock.service';
import { sweetAlert } from 'src/app/utils/swal';
import { getstandingOrderDays } from 'src/app/utils/utils.common';
import { DriversService } from 'src/app/component/drivers/drivers-service/drivers-service';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';

@Component({
  selector: 'app-mapper-modal',
  templateUrl: './mapper-modal.component.html',
  styleUrls: ['./mapper-modal.component.css']
})
export class MapperModalComponent implements OnInit, OnDestroy {
  @Output() closeModal = new EventEmitter<any>();
  mappedItems: any[] = [];
  searchTerm: string = '';
  isAdding = false;
  addForm: FormGroup;
  driverList: any[] = [];
  preDriversMap: { [key: string]: string } = {};
  
  editingItemId: string | null = null;
  editingValue = '';

  constructor(
    private clientLockService: ClientLockService,
    private driverService: DriversService,
    private sharedDataService: SharedDataService,
    private fb: FormBuilder
  ) { }

  get recurringDaysControls() {
    return (this.addForm.get('recurringDays') as FormArray).controls;
  }

  get filteredItems() {
    if (!this.searchTerm) return this.mappedItems;
    const lowerTerm = this.searchTerm.toLowerCase();
    return this.mappedItems.filter(item => item.clientId && item.clientId.toLowerCase().includes(lowerTerm));
  }

  ngOnInit() {
    this.addForm = this.fb.group({
      newId: ['', Validators.required],
      recurringDays: this.fb.array([])
    });

    const recurringDaysArr = this.addForm.get('recurringDays') as FormArray;
    for (let dayItem of getstandingOrderDays) {
      recurringDaysArr.push(this.fb.group({
        label: [dayItem.label],
        day: [dayItem.day],
        driver: [null]
      }));
    }

    this.fetchIds();

    this.sharedDataService.getUser().pipe(takeUntil(componentDestroyed(this))).subscribe(user => {
      if (user) {
        this.getDriverList(user._id);
      }
    });
  }

  getDriverList(companyId) {
    this.driverService.getDrivers(companyId, 'active')
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          this.driverList = data.filter((driver: any) => driver.isLocked);
          this.preDriversMap = {};
          this.driverList.forEach((driver: any) => {
            this.preDriversMap[driver._id] = driver.displayName;
          });
        }
      });
  }

  ngOnDestroy() {
  }

  onCloseModal() {
    this.closeModal.emit();
  }

  fetchIds() {
    this.clientLockService.getClientLockIds()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.mappedItems = data;
          }
        },
        (err) => {
          sweetAlert('Error', err || 'Failed to fetch ID list.', 'error', 'Ok');
        }
      );
  }

  startAdding() {
    this.isAdding = true;
    this.editingItemId = null;
    
    // Reset specific fields to preserve FormArray defaults
    this.addForm.get('newId').setValue('');
    const recurringDaysArr = this.addForm.get('recurringDays') as FormArray;
    recurringDaysArr.controls.forEach(ctrl => {
      ctrl.get('driver').setValue(null);
    });

    this.cancelEditingRow();
  }

  cancelAdding() {
    this.isAdding = false;
    this.addForm.get('newId').setValue('');
    const recurringDaysArr = this.addForm.get('recurringDays') as FormArray;
    recurringDaysArr.controls.forEach(ctrl => {
      ctrl.get('driver').setValue(null);
    });
    this.cancelEditingRow();
  }

  submitId() {
    if (this.addForm.invalid) {
      this.addForm.markAllAsTouched();
      return;
    }
    
    const newIdValue = this.addForm.get('newId').value;
    if (!newIdValue || newIdValue.trim() === '') {
      sweetAlert('Warning', 'Please enter a valid ID.', 'warning', 'Ok');
      return;
    }
    const cleanId = newIdValue.trim();
    
    const isUpdating = this.editingItemId !== null;

    const cleanIdLower = cleanId.toLowerCase();
    if (this.mappedItems.some((item) => (isUpdating ? item._id !== this.editingItemId : true) && (item.clientId || '').toLowerCase() === cleanIdLower)) {
      sweetAlert('Warning', 'This ID is already in the list.', 'warning', 'Ok');
      return;
    }

    const payload: any = { clientId: cleanId };
    
    const formDays = this.addForm.get('recurringDays').value;
    const selectedDays = formDays
      .filter((d: any) => d.driver !== null && d.driver !== 'null')
      .map((d: any) => ({ day: d.day, driver: d.driver }));
    if (selectedDays.length > 0) {
      payload.recurringDays = selectedDays;
    }

    if (isUpdating) {
      const item = this.mappedItems.find(i => i._id === this.editingItemId);
      if (!item) return;
      this.clientLockService.updateClientLockId(item._id, payload)
        .pipe(takeUntil(componentDestroyed(this)))
        .subscribe(
          (updatedRecord) => {
            this.fetchIds();
            sweetAlert('Success', 'ID updated successfully.', 'success', 'Ok');
            this.cancelAdding();
          },
          (err) => {
            sweetAlert('Error', err || 'Failed to update ID.', 'error', 'Ok');
          }
        );
    } else {
      this.clientLockService.addClientLockId(payload)
        .pipe(takeUntil(componentDestroyed(this)))
        .subscribe(
          (newRecord) => {
            this.fetchIds();
            sweetAlert('Success', 'ID added successfully.', 'success', 'Ok');
            this.cancelAdding();
          },
          (err) => {
            sweetAlert('Error', err || 'Failed to add ID.', 'error', 'Ok');
          }
        );
    }
  }

  startEditing(item: any) {
    this.editingItemId = item._id;
    this.isAdding = true;

    this.addForm.patchValue({
      newId: item.clientId
    });

    const recurringDaysArr = this.addForm.get('recurringDays') as FormArray;
    recurringDaysArr.controls.forEach(ctrl => {
      const day = ctrl.get('day').value;
      const matchingDay = item.recurringDays && item.recurringDays.find((d: any) => d.day === day);
      if (matchingDay && matchingDay.driver) {
        const driverVal = typeof matchingDay.driver === 'object' ? matchingDay.driver._id : matchingDay.driver;
        ctrl.get('driver').setValue(driverVal);
      } else {
        ctrl.get('driver').setValue(null);
      }
    });
  }

  cancelEditingRow() {
    this.editingItemId = null;
  }

  getDayLabel(day: number) {
    const dayObj = getstandingOrderDays.find(d => d.day === day);
    return dayObj ? dayObj.label : 'Unknown';
  }

  getDriverName(driver: any) {
    if (!driver) return 'N/A';
    const driverId = typeof driver === 'object' ? driver._id : driver;
    return this.preDriversMap[driverId] || 'Unknown Driver';
  }

  deleteRecord(item: any) {
    sweetAlert('Alert', 'Are you sure you want to delete this record?', 'warning', 'Yes', 'No')
      .then((result) => {
        if (result.value) {
          this.clientLockService.deleteClientLockId(item._id)
            .pipe(takeUntil(componentDestroyed(this)))
            .subscribe(
              () => {
                sweetAlert('Success', 'ID deleted successfully.', 'success', 'Ok');
                this.fetchIds();
              },
              (err) => {
                sweetAlert('Error', err || 'Failed to delete ID.', 'error', 'Ok');
              }
            );
        }
      });
  }
}
